import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Building2, Search } from 'lucide-react';
import { z } from 'zod';
import { client } from '@/lib/api';
import { createVendorSchema, type CreateVendorInput } from '@everplan/shared';
import { mergeCategorySuggestions } from '@/lib/categories';
import { CategoryFilter } from '@/components/CategoryFilter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CategoryChipInput } from '@/components/CategoryChipInput';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Input type for the form (before Zod transforms/defaults are applied)
type CreateVendorFormInput = z.input<typeof createVendorSchema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function InitialAvatar({ name }: { name: string }) {
  const letter = name.charAt(0).toUpperCase();
  return (
    <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
      {letter}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-Vendor Modal (inline — ~80 lines)
// ---------------------------------------------------------------------------
interface AddVendorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestions: string[];
}

function AddVendorModal({ open, onOpenChange, suggestions }: AddVendorModalProps) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateVendorFormInput, unknown, CreateVendorInput>({
    resolver: zodResolver(createVendorSchema),
    defaultValues: {
      name: '',
      email: null,
      phone: null,
      website: null,
      notes: null,
      categoryNames: [],
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateVendorInput) => {
      const res = await client.api.vendors.$post({ json: data });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = (body as { error?: string }).error ?? 'Failed to create vendor';
        throw new Error(msg);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-category-suggestions'] });
      onOpenChange(false);
      reset();
    },
  });

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) reset();
      onOpenChange(open);
    },
    [onOpenChange, reset],
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Vendor</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit((data) => createMutation.mutate(data))}
          className="space-y-4"
        >
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="v-name">
              <span className="label-caps text-xs text-muted-foreground">Vendor Name *</span>
            </Label>
            <Input
              id="v-name"
              placeholder="e.g. Sweet Scoops Gelato Cart"
              {...register('name')}
              aria-invalid={!!errors.name}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Categories chip input */}
          <div className="space-y-1.5">
            <span className="label-caps text-xs text-muted-foreground">Categories</span>
            <Controller
              control={control}
              name="categoryNames"
              render={({ field }) => (
                <CategoryChipInput
                  value={field.value ?? []}
                  onChange={field.onChange}
                  suggestions={suggestions}
                />
              )}
            />
          </div>

          {/* Contact fields row */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="v-email">
                <span className="label-caps text-xs text-muted-foreground">Email</span>
              </Label>
              <Input
                id="v-email"
                type="email"
                placeholder="vendor@example.com"
                {...register('email')}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email?.message as string}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="v-phone">
                <span className="label-caps text-xs text-muted-foreground">Phone</span>
              </Label>
              <Input id="v-phone" type="tel" placeholder="(555) 123-4567" {...register('phone')} />
            </div>
          </div>

          {/* Website */}
          <div className="space-y-1.5">
            <Label htmlFor="v-website">
              <span className="label-caps text-xs text-muted-foreground">Website</span>
            </Label>
            <Input
              id="v-website"
              type="text"
              placeholder="example.com"
              {...register('website')}
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label htmlFor="v-notes">
              <span className="label-caps text-xs text-muted-foreground">Notes</span>
            </Label>
            <Textarea id="v-notes" placeholder="Any notes about this vendor…" {...register('notes')} />
          </div>

          {createMutation.isError && (
            <p className="text-sm text-destructive">
              {createMutation.error?.message ?? 'Something went wrong. Please try again.'}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Adding…' : 'Add Vendor'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// VendorsPage
// ---------------------------------------------------------------------------
export default function VendorsPage() {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('name');
  const [showModal, setShowModal] = useState(false);

  const { data: vendors = [], isLoading, isError } = useQuery({
    queryKey: ['vendors', { search, category, sort }],
    queryFn: async () => {
      const res = await client.api.vendors.$get({
        query: {
          ...(search ? { search } : {}),
          ...(category ? { category } : {}),
          sort,
        },
      });
      if (!res.ok) throw new Error('Failed to load vendors');
      return res.json();
    },
  });

  const { data: categorySuggestions = [] } = useQuery({
    queryKey: ['vendor-category-suggestions'],
    queryFn: async () => {
      const res = await client.api.vendors['category-suggestions'].$get();
      if (!res.ok) throw new Error('Failed to load suggestions');
      return res.json() as Promise<string[]>;
    },
  });

  const suggestions = useMemo(() => mergeCategorySuggestions(categorySuggestions), [categorySuggestions]);

  const markedViewedRef = useRef(false);
  useEffect(() => {
    if (markedViewedRef.current) return;
    // Set the ref only after the request resolves so a transient network
    // failure leaves the call eligible for retry on the next mount.
    client.api.vendors['mark-viewed']
      .$post()
      .then(() => {
        markedViewedRef.current = true;
      })
      .catch(() => {
        // Pill remains on next visit; no functional consequence. No user-facing error.
      });
  }, []);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <h1 className="display-tight text-3xl font-semibold">Vendors</h1>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="size-4" />
          New Vendor
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* TODO: replace explicit submit with debounced live search (250ms). */}
        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="h-9 w-52 pl-9"
              placeholder="Search vendors…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <Button type="submit" variant="outline" size="sm">
            Search
          </Button>
          {search && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch('');
                setSearchInput('');
              }}
            >
              Clear
            </Button>
          )}
        </form>

        {/* Category filter */}
        <CategoryFilter
          value={category}
          onChange={setCategory}
          suggestions={suggestions}
          placeholder="Filter category…"
        />

        {/* Sort */}
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="lastBooked">Last Booked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Loading */}
      {isLoading && (
        <p className="display-tight text-lg text-muted-foreground">Loading vendors…</p>
      )}

      {/* Error */}
      {isError && (
        <Card className="rounded-3xl border-destructive/20 shadow-ambient">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">Failed to load vendors. Please refresh.</p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!isLoading && !isError && vendors.length === 0 && (
        <Card className="rounded-3xl bg-card shadow-ambient">
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <Building2 className="size-12 text-muted-foreground/40" />
            <div>
              <p className="display-tight text-lg font-medium">No vendors yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Add your first vendor to start building your rolodex.
              </p>
            </div>
            <Button onClick={() => setShowModal(true)}>
              <Plus className="size-4" />
              New Vendor
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Vendor list */}
      {!isLoading && !isError && vendors.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-border shadow-ambient">
          {vendors.map((vendor, idx) => (
            <Link
              key={vendor.id}
              to={`/vendors/${vendor.id}`}
              className={`flex items-center gap-4 bg-card px-6 py-4 transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                idx !== vendors.length - 1 ? 'border-b border-border' : ''
              }`}
            >
              <InitialAvatar name={vendor.name} />

              <div className="flex min-w-0 flex-1 items-center gap-2">
                <div className="min-w-0">
                  <p className="truncate font-serif font-semibold leading-snug">{vendor.name}</p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {[vendor.email, vendor.phone].filter(Boolean).join(' · ') || 'No contact info'}
                  </p>
                  {vendor.categoryNames.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {vendor.categoryNames.slice(0, 3).map((name) => (
                        <Badge key={name} variant="secondary" className="text-xs">
                          {name}
                        </Badge>
                      ))}
                      {vendor.categoryNames.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{vendor.categoryNames.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                {vendor.firstViewedAt === null && (
                  <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                    New
                  </span>
                )}
              </div>

              {vendor.website && (
                <div className="shrink-0">
                  <Badge variant="outline" className="text-xs">
                    Website
                  </Badge>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {/* Add vendor modal */}
      <AddVendorModal
        open={showModal}
        onOpenChange={setShowModal}
        suggestions={suggestions}
      />
    </div>
  );
}
